import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	TextField,
	Theme,
	makeStyles,
} from "@material-ui/core";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { useSettingsContext } from "../../context/SettingsContext";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Setting } from "../../models/Setting";
import { upsertSettings } from "../../repositories/settingRepository";

const useStyles = makeStyles((theme: Theme) => ({
	title: {
		fontSize: 15,
	},
	modalTitle: {
		fontSize: 15,
		marginTop: theme.spacing(1),
	},
	chip: {
		margin: 2,
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
	infoLabel: {
		fontSize: 12,
		color: "#757575",
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditSetting = ({ show, onHide }: Props) => {
	const [open, setOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const {
		state: { settingSelected },
	} = useSettingsContext();

	const { control, handleSubmit, reset, setValue } = useForm<Setting>({
		defaultValues: settingSelected ? settingSelected : INITIAL_VALUES,
	});

	const onSubmit: SubmitHandler<Setting> = async (data) => {
		setIsSubmitLoading(true);
		const settingsResp = await upsertSettings(
			data,
			settingSelected?.SettingName,
		);
		if (settingsResp.ok) {
			setIsSubmitLoading(false);
			setOpen(true);
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(settingsResp.message);
		}
	};

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	useEffect(() => {
		if (show) {
			reset();
			if (settingSelected) {
				setValue("SettingId", settingSelected.SettingId);
				setValue("SettingName", settingSelected.SettingName);
				setValue("Value", settingSelected.Value);
				setValue("Description", settingSelected.Description);
			}
		}
	}, [show]);

	return (
		<>
			<Grid container>
				<Grid item>
					<MesfModal
						title="EDIT SETTING/CREW"
						open={show}
						handleClose={() => onHide(false)}
						id="edit-setting-modal"
						maxWidth="md"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content style={{ padding: "15px 30px 0" }}>
								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										<Controller
											name="SettingName"
											control={control}
											rules={{ required: "Name is required" }}
											render={({ field, fieldState: { error } }) => (
												<TextField
													{...field}
													label="Name"
													variant="outlined"
													error={!!error}
													helperText={error ? error.message : null}
													fullWidth
													margin="dense"
													autoComplete="off"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<Controller
											name="Value"
											control={control}
											rules={{
												required: "Value is required",
												pattern: {
													value: /^[A-Za-z0-9]+$/,
													message: "Only letters and numbers are allowed",
												},
											}}
											render={({ field, fieldState: { error } }) => (
												<TextField
													{...field}
													label="Value"
													variant="outlined"
													error={!!error}
													helperText={error ? error.message : null}
													fullWidth
													margin="dense"
													autoComplete="off"
												/>
											)}
										/>
									</Grid>
									<Grid
										item
										md={12}
										xs={12}
										style={{ margin: "12px 0 20px", padding: "0 8px" }}
									>
										<Controller
											name="Description"
											control={control}
											render={({ field: { value, onChange } }) => (
												<TextField
													style={{ margin: "8px 0" }}
													size="medium"
													maxRows={5}
													label="Description"
													multiline
													variant="outlined"
													fullWidth
													autoComplete="off"
													value={value}
													onChange={(evt) => {
														if (evt.target.value.length <= 200) onChange(evt);
													}}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="default"
											onClick={() => onHide(false)}
										>
											Cancel
										</Button>
									</Grid>
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
										>
											Save
										</Button>
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid>
			</Grid>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error updating Setting"
			/>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="success" onClose={handleClose}>
					The setting was edited successfully
				</Alert>
			</Snackbar>
		</>
	);
};
